<template>
    <div class="medialibrary__edit">
        <div class="medialibrary__edit_header">
            <view-actions
                :title="$t('lms.mediaLibrary.titles.mediaLibrary')"
                :action-button-active="false"
            />
            <component-navigator
                ref="componentnavigator"
                :back-to="{ name: 'media-library-list' }"
                :previous-to="siblingsPrevId ? { name: 'media-library-edit', params: { id: siblingsPrevId } } : undefined"
                :next-to="siblingsNextId ? { name: 'media-library-edit', params: { id: siblingsNextId } } : undefined"
                :remove-notify-title="$t('lms.mediaLibrary.navigator.titleNavigator')"
                :remove-notify-text="$t('lms.mediaLibrary.navigator.areYouSure')"
                @emit-remove="handleRemoveMedia"
            />
        </div>
        <div class="medialibrary__edit_main">
            <div class="container">
                <div class="columns is-block-mobile is-variable">
                    <div class="column is-7-desktop">
                        <div class="medialibrary__edit_main_thumbnail">
                            <component-thumbnail
                                :id="id"
                                :alt="alt"
                                :thumbnail-size="'large'"
                                :allow-preview="true"
                                :pop-image-size="'original'"
                            />
                        </div>
                        <div class="medialibrary__edit_main_meta">
                            <div class="columns is-multiline">
                                <div class="column is-12-tablet is-4-desktop">
                                    <component-text>
                                        <strong>{{ $t('lms.mediaLibrary.text.fileName') }}</strong><br>
                                        {{ fileName }}
                                    </component-text>
                                </div>
                                <div class="column is-12-tablet is-4-desktop">
                                    <component-text>
                                        <strong>{{ $t('lms.mediaLibrary.text.fileType') }}</strong><br>
                                        {{ fileType }}
                                    </component-text>
                                </div>
                                <div class="column is-12-tablet is-4-desktop">
                                    <component-text>
                                        <strong>{{ $t('lms.mediaLibrary.text.uploadDate') }}</strong><br>
                                        {{ dateUploaded }}
                                    </component-text>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="column is-tablet-padding-left-4 is-desktop-padding-left-6 is-5-desktop">
                        <div class="medialibrary__edit_main_details">
                            <form-text
                                :label="$t('lms.mediaLibrary.labels.title')"
                                :name="'media-library-edit-title'"
                                :v-validate="'required'"
                                :required="true"
                                :data-vv-as="'title'"
                                v-model="title"
                            />
                            <form-textarea
                                :label="$t('lms.mediaLibrary.labels.description')"
                                :name="'media-library-edit-description'"
                                v-model="description"
                            />
                            <form-text
                                :label="$t('lms.mediaLibrary.labels.altText')"
                                :name="'media-library-edit-alt-text'"
                                v-model="alt"
                            />
                            <form-checkbox
                                v-if="loggedInUserId === uploaderId"
                                :name="'media-library-edit-is-private'"
                                :label="$t('lms.mediaLibrary.labels.isPrivate')"
                                v-model="isPrivate"
                            >{{ $t('lms.mediaLibrary.text.checkThisOption') }}</form-checkbox>
                        </div>
                        <div class="medialibrary__edit_main_actions">
                            <form-submit
                                @reset="performReset"
                                @submit="performSubmitValidate"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
    </div>
</template>

<script>
import ViewActions from '@/components/default/shared/ViewActions'
import ComponentNavigator from '@/components/default/shared/ComponentNavigator'
import ComponentText from '@/components/default/shared/ComponentText'
import FormText from '@/components/default/forms/FormText'
import FormTextarea from '@/components/default/forms/FormTextarea'
import ComponentThumbnail from '@/components/default/shared/ComponentThumbnail'
import { toLocaleDateTime } from '@/functions'
import FormSubmit from '@/components/default/forms/FormSubmit'
import { performReset, performSubmitValidate, validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import PopNotify from '@/components/default/shared/PopNotify'
import FormCheckbox from '@/components/default/forms/FormCheckbox'

export default {
  name: 'media-library-edit',
  components: { FormCheckbox, PopNotify, FormSubmit, ComponentThumbnail, FormTextarea, FormText, ComponentText, ComponentNavigator, ViewActions },
  mixins: [validateBeforeRouteLeave, performReset, performSubmitValidate, watchContentChangeEmit],
  computed: {
    loggedInUserId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataID
    },
    //
    title: {
      get () {
        return this.$store.getters.getStorageFileTitle
      },
      set (value) {
        this.$store.dispatch('storageFileSetTitle', value)
      }
    },
    description: {
      get () {
        return this.$store.getters.getStorageFileDescription
      },
      set (value) {
        this.$store.dispatch('storageFileSetDescription', value)
      }
    },
    alt: {
      get () {
        return this.$store.getters.getStorageFileAlt
      },
      set (value) {
        this.$store.dispatch('storageFileSetAlt', value)
      }
    },
    isPrivate: {
      get () {
        return this.$store.getters.getStorageFileIsPrivate
      },
      set (value) {
        this.$store.dispatch('storageFileSetIsPrivate', value)
      }
    },
    siblingsPrevId () {
      return this.$store.getters.getStorageFileSiblingsPrevId
    },
    siblingsNextId () {
      return this.$store.getters.getStorageFileSiblingsNextId
    },
    id () {
      return this.$store.getters.getStorageFileId
    },
    fileName () {
      return this.$store.getters.getStorageFileOriginalFileName
    },
    fileType () {
      return this.$store.getters.getStorageFileFileType
    },
    uploaderId () {
      return this.$store.getters.getStorageFileUploaderId
    },
    dateUploaded () {
      return toLocaleDateTime(this.$store.getters.getStorageFileDateUploaded)
    },
    presignedUrls () {
      return this.$store.getters.getStorageFilePresignedUrls
    },
    //
    watchContent () {
      return this.title + this.description + this.alt
    },
    //
    rootItemName () {
      return 'media-library'
    },
    nameActionUpdate () {
      return 'storageFileUpdate'
    },
    nameActionReset () {
      return 'storageFileReset'
    }
  },
  watch: {
    id (value) {
      this.clearContentChange()
    }
  },
  methods: {
    handleRemoveMedia () {
      this.$store.dispatch('storageFileRemove', this.id).then(() => {
        this.$router.push({ name: 'media-library-list' })
      }).finally(() => {
        this.$eventBus.$emit('stop-remove-media-notify')
        this.$refs.componentnavigator.$refs.componentbuttonremove.$refs.componentpopnotify.closeNotify()
      })
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/media-library/_medialibraryedit.scss";
</style>
